import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Shell from "@/components/Shell";
import StatusBadge from "@/components/StatusBadge";
import { api, formatDate, STATUS_LABELS, STATUS_ORDER } from "@/lib/api";
import { ArrowUpRight, Inbox, Users, Activity } from "lucide-react";

export default function AdminDashboard() {
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("ALL");
  const nav = useNavigate();

  const load = async () => {
    const [{ data: cs }, { data: st }] = await Promise.all([
      api.get("/cases"), api.get("/admin/stats"),
    ]);
    setCases(cs || []);
    setStats(st || null);
  };

  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);

  const filtered = filter === "ALL" ? cases : cases.filter((c) => c.status === filter);

  return (
    <Shell>
      <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-10">
        <div className="text-xs uppercase tracking-[0.2em] text-emerald-brand font-semibold">Admin Console</div>
        <div className="flex flex-wrap items-end justify-between gap-4 mt-2 mb-8">
          <h1 className="font-heading font-bold text-4xl md:text-5xl">Case Inbox</h1>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold inline-flex items-center gap-4">
            <span className="inline-flex items-center gap-2"><Activity className="w-3.5 h-3.5 text-emerald-brand" /> {stats?.total ?? 0} cases</span>
            <span className="inline-flex items-center gap-2"><Users className="w-3.5 h-3.5 text-gold" /> {stats?.clients ?? 0} clients</span>
          </div>
        </div>

        {/* Filter tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <Tile testId="filter-all" label="All" value={stats?.total ?? 0} active={filter === "ALL"} onClick={() => setFilter("ALL")} accent="navy" />
          {STATUS_ORDER.map((s, i) => (
            <Tile
              key={s}
              testId={`filter-${s}`}
              label={STATUS_LABELS[s]}
              value={stats?.[s] ?? 0}
              active={filter === s}
              onClick={() => setFilter(s)}
              accent={i === STATUS_ORDER.length - 1 ? "emerald" : "navy"}
            />
          ))}
        </div>

        <div className="mt-10 brand-card p-0 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold inline-flex items-center gap-2">
              <Inbox className="w-3.5 h-3.5" /> Case list
            </span>
            <span className="text-xs text-slate-500">{filtered.length} showing</span>
          </div>

          {filtered.length === 0 ? (
            <div className="p-12 text-center text-xs uppercase tracking-wider text-slate-400">
              No cases match this filter
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 font-semibold border-b border-slate-100">
                  <th className="py-3 px-4">Case №</th>
                  <th className="py-3 px-4">Client</th>
                  <th className="py-3 px-4">Title</th>
                  <th className="py-3 px-4">Insurance</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Updated</th>
                  <th className="py-3 px-4 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr
                    key={c.id}
                    data-testid={`admin-case-row-${c.id}`}
                    onClick={() => nav(`/admin/cases/${c.id}`)}
                    className="border-b border-slate-50 last:border-b-0 hover:bg-slate-50 cursor-pointer transition-colors"
                  >
                    <td className="py-4 px-4 text-xs font-mono text-emerald-brand font-semibold">{c.case_no}</td>
                    <td className="py-4 px-4">
                      <div className="font-medium">{c.client_name}</div>
                      <div className="text-[11px] text-slate-500">{c.client_email}</div>
                    </td>
                    <td className="py-4 px-4 font-medium">{c.title}</td>
                    <td className="py-4 px-4 text-xs text-slate-600">{c.category}</td>
                    <td className="py-4 px-4"><StatusBadge status={c.status} /></td>
                    <td className="py-4 px-4 text-xs text-slate-500">{formatDate(c.updated_at)}</td>
                    <td className="py-4 px-4 text-right"><ArrowUpRight className="w-4 h-4 inline text-slate-400" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Shell>
  );
}

function Tile({ label, value, active, onClick, testId, accent }) {
  const activeBg = accent === "emerald" ? "var(--emerald)" : "var(--navy)";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      style={active ? { background: activeBg, borderColor: activeBg } : {}}
      className={`rounded-2xl p-4 text-left transition-all border ${active ? "shadow-lg" : "bg-white border-slate-100 hover:-translate-y-0.5 hover:shadow-md"}`}
    >
      <div className={`text-[10px] uppercase tracking-wider font-semibold ${active ? "text-white/80" : "text-slate-500"}`}>{label}</div>
      <div className={`font-heading font-bold text-2xl md:text-3xl mt-1 ${active ? "text-white" : "text-navy"}`}>{value}</div>
    </button>
  );
}
